import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError, type UserOut } from "@/api/client";

type AuthState =
  | { status: "loading" }
  | { status: "anon" }
  // /me не ответил вовсе (нет сети, 502 на выкатке) — это не «не вошёл».
  // Полная версия показывает вход, как раньше; мобильная — «нет связи».
  | { status: "unreachable" }
  | { status: "authed"; user: UserOut };

interface AuthCtx {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: UserOut) => void;
  /** Спросить /me ещё раз (после «нет связи»). */
  retry: () => void;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  const check = useCallback(() => {
    api
      .me()
      .then((user) => setState({ status: "authed", user }))
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) setState({ status: "anon" });
        else if (e instanceof ApiError && e.status < 500) setState({ status: "anon" });
        else setState({ status: "unreachable" });
      });
  }, []);
  useEffect(check, [check]);

  const login = async (email: string, password: string) => {
    const user = await api.login(email, password);
    setState({ status: "authed", user });
  };

  const logout = async () => {
    await api.logout().catch(() => {});
    setState({ status: "anon" });
  };

  const setUser = (user: UserOut) => setState({ status: "authed", user });

  return (
    <Ctx.Provider value={{ state, login, logout, setUser, retry: check }}>{children}</Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
